import React from 'react';
import { DeliveryNote } from '../../types/deliveryNote';
import Modal from '../common/Modal';
import DocumentChain from '../common/DocumentChain';
import { 
  TruckIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  DocumentTextIcon,
  UserIcon,
  BuildingOffice2Icon,
  PhoneIcon,
  EnvelopeIcon,
  MapPinIcon,
  ClockIcon,
  CubeIcon,
  LinkIcon
} from '@heroicons/react/24/outline';
import { useLanguage } from '../../contexts/LanguageContext';
import { formatCurrency } from '../../utils/formatters';

interface DeliveryNoteViewProps {
  deliveryNote: DeliveryNote | null;
  isOpen: boolean;
  onClose: () => void;
}

const getStatusColor = (status: string) => {
  switch (status) {
    case 'draft': return 'text-gray-600 bg-gray-100';
    case 'pending': return 'text-yellow-600 bg-yellow-100';
    case 'shipped': return 'text-blue-600 bg-blue-100';
    case 'delivered': return 'text-green-600 bg-green-100';
    case 'confirmed': return 'text-emerald-600 bg-emerald-100';
    case 'cancelled': return 'text-red-600 bg-red-100';
    default: return 'text-gray-600 bg-gray-100';
  }
};

const getStatusLabel = (status: string) => {
  switch (status) {
    case 'draft': return 'Piszkozat';
    case 'pending': return 'Függőben';
    case 'shipped': return 'Szállítás alatt';
    case 'delivered': return 'Kiszállítva';
    case 'confirmed': return 'Megerősítve';
    case 'cancelled': return 'Törölve';
    default: return status;
  }
};

const DeliveryNoteView: React.FC<DeliveryNoteViewProps> = ({ deliveryNote, isOpen, onClose }) => {
  if (!deliveryNote) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Szállítólevél részletei - ${deliveryNote.number}`}
      size="6xl"
    >
      <div className="space-y-6 p-6 max-h-[80vh] overflow-y-auto">
        {/* Fejléc információk */}
        <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="flex">
            <div className="p-4 bg-gradient-to-br from-blue-100 to-indigo-100 dark:from-blue-900/20 dark:to-indigo-900/20 flex items-center justify-center rounded-l-lg border-r border-gray-200 dark:border-gray-600">
              <TruckIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="p-4 flex-1">
              <h3 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-4">Szállítólevél információk</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Szállítólevél száma:</span>
                  <span className="text-sm font-medium">{deliveryNote.number}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Dátum:</span>
                  <span className="text-sm font-medium">
                    {new Date(deliveryNote.deliveryDate).toLocaleDateString('hu-HU')}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Státusz:</span>
                  <span className={`text-xs px-2 py-1 rounded-full font-medium ${getStatusColor(deliveryNote.status)}`}>
                    {getStatusLabel(deliveryNote.status)}
                  </span>
                </div>
                {deliveryNote.totalAmount && (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Összeg:</span>
                    <span className="text-sm font-semibold text-blue-600 dark:text-blue-400">
                      {formatCurrency(deliveryNote.totalAmount, deliveryNote.currency)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Bizonylat lánc */}
        <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="flex">
            <div className="p-4 bg-gradient-to-br from-indigo-100 to-purple-100 dark:from-indigo-900/20 dark:to-purple-900/20 flex items-center justify-center rounded-l-lg border-r border-gray-200 dark:border-gray-600">
              <LinkIcon className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
            </div>
            <div className="p-4 flex-1">
              <h3 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-4">Bizonylat lánc</h3>
              <DocumentChain
                documentId={deliveryNote.id}
                documentType="delivery_note"
                className="text-sm"
                compact={true}
              />
            </div>
          </div>
        </div>

        {/* Ügyfél információk */}
        <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="flex">
            <div className="p-4 bg-gradient-to-br from-emerald-100 to-teal-100 dark:from-emerald-900/20 dark:to-teal-900/20 flex items-center justify-center rounded-l-lg border-r border-gray-200 dark:border-gray-600">
              <UserIcon className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div className="p-4 flex-1">
              <h3 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-4">Ügyfél adatok</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Név:</span>
                  <span className="text-sm font-medium">{deliveryNote.customerName}</span>
                </div>
                {deliveryNote.customerEmail && (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Email:</span>
                    <span className="text-sm font-medium">{deliveryNote.customerEmail}</span>
                  </div>
                )}
                {deliveryNote.customerPhone && (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Telefon:</span>
                    <span className="text-sm font-medium">{deliveryNote.customerPhone}</span>
                  </div>
                )}
                {deliveryNote.customerAddress && (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Cím:</span>
                    <span className="text-sm font-medium">{deliveryNote.customerAddress}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Szállítási információk */}
        <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="flex">
            <div className="p-4 bg-gradient-to-br from-orange-100 to-red-100 dark:from-orange-900/20 dark:to-red-900/20 flex items-center justify-center rounded-l-lg border-r border-gray-200 dark:border-gray-600">
              <MapPinIcon className="w-5 h-5 text-orange-600 dark:text-orange-400" />
            </div>
            <div className="p-4 flex-1">
              <h3 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-4">Szállítási adatok</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {deliveryNote.deliveryAddress && (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Szállítási cím:</span>
                    <span className="text-sm font-medium">{deliveryNote.deliveryAddress}</span>
                  </div>
                )}
                {deliveryNote.trackingNumber && (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Csomagszám:</span>
                    <span className="text-sm font-medium text-blue-600 dark:text-blue-400">
                      {deliveryNote.trackingNumber}
                    </span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Szállítási dátum:</span>
                  <span className="text-sm font-medium">
                    {new Date(deliveryNote.deliveryDate).toLocaleDateString('hu-HU')}
                  </span>
                </div>
                {deliveryNote.expectedDeliveryDate && (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Várható érkezés:</span>
                    <span className="text-sm font-medium">
                      {new Date(deliveryNote.expectedDeliveryDate).toLocaleDateString('hu-HU')}
                    </span>
                  </div>
                )}
              </div>
              {deliveryNote.deliveryInstructions && (
                <div className="mt-4">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Szállítási utasítások:</span>
                  <p className="text-sm font-medium mt-1">{deliveryNote.deliveryInstructions}</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Szállítási tételek */}
        {deliveryNote.items && deliveryNote.items.length > 0 && (
          <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="flex">
              <div className="p-4 bg-gradient-to-br from-cyan-100 to-sky-100 dark:from-cyan-900/20 dark:to-sky-900/20 flex items-center justify-center rounded-l-lg border-r border-gray-200 dark:border-gray-600">
                <CubeIcon className="w-5 h-5 text-cyan-600 dark:text-cyan-400" />
              </div>
              <div className="p-4 flex-1">
                <h3 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-4">Szállítási tételek</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Termék
                        </th>
                        <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Mennyiség
                        </th>
                        {deliveryNote.totalAmount && (
                          <>
                            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                              Egységár
                            </th>
                            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                              Összeg
                            </th>
                          </>
                        )}
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {deliveryNote.items.map((item, index) => (
                        <tr key={index}>
                          <td className="px-3 py-2 text-sm">
                            <div className="font-medium text-gray-900 dark:text-gray-100">
                              {item.productName}
                            </div>
                            {item.productCode && (
                              <div className="text-gray-500 dark:text-gray-400 text-xs">
                                Cikkszám: {item.productCode}
                              </div>
                            )}
                            {item.notes && (
                              <div className="text-gray-500 dark:text-gray-400 text-xs">
                                {item.notes}
                              </div>
                            )}
                          </td>
                          <td className="px-3 py-2 text-center text-sm text-gray-900 dark:text-gray-100">
                            {item.deliveredQuantity} / {item.orderedQuantity}
                          </td>
                          {deliveryNote.totalAmount && (
                            <>
                              <td className="px-3 py-2 text-right text-sm text-gray-900 dark:text-gray-100">
                                {item.unitPrice ? formatCurrency(item.unitPrice, deliveryNote.currency) : '-'}
                              </td>
                              <td className="px-3 py-2 text-right text-sm font-medium text-gray-900 dark:text-gray-100">
                                {item.lineTotal ? formatCurrency(item.lineTotal, deliveryNote.currency) : '-'}
                              </td>
                            </>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Megjegyzések */}
        {deliveryNote.notes && (
          <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="flex">
              <div className="p-4 bg-gradient-to-br from-slate-100 to-gray-100 dark:from-slate-900/20 dark:to-gray-900/20 flex items-center justify-center rounded-l-lg border-r border-gray-200 dark:border-gray-600">
                <DocumentTextIcon className="w-5 h-5 text-slate-600 dark:text-slate-400" />
              </div>
              <div className="p-4 flex-1">
                <h3 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-4">Megjegyzések</h3>
                <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                  {deliveryNote.notes}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Rendszer információk */}
        <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="flex">
            <div className="p-4 bg-gradient-to-br from-violet-100 to-purple-100 dark:from-violet-900/20 dark:to-purple-900/20 flex items-center justify-center rounded-l-lg border-r border-gray-200 dark:border-gray-600">
              <BuildingOffice2Icon className="w-5 h-5 text-violet-600 dark:text-violet-400" />
            </div>
            <div className="p-4 flex-1">
              <h3 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-4">Rendszer információk</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs text-gray-500 dark:text-gray-400">
                <div className="flex justify-between">
                  <span>Létrehozva:</span>
                  <span>{new Date(deliveryNote.createdAt).toLocaleDateString('hu-HU')}</span>
                </div>
                {/* Módosítva dátum csak akkor, ha különbözik a létrehozás dátumától */}
                {new Date(deliveryNote.createdAt).toDateString() !== new Date(deliveryNote.updatedAt).toDateString() && (
                  <div className="flex justify-between">
                    <span>Módosítva:</span>
                    <span>{new Date(deliveryNote.updatedAt).toLocaleDateString('hu-HU')}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Bezárás gomb */}
        <div className="flex justify-end pt-6 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            Bezárás
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default DeliveryNoteView; 